import { a11yProps, gridSizeMdLg12 } from "@/shared";
import { MaterialTransferido } from "@/shared/interfaces";
import { Alert, AlertTitle, Box, Grid, Tab, Tabs } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useCargarSeriesProductos } from "@/store/erp/productos/useCargarSeriesProductos.store";
import useTableRevisionSolicitudPedido from "../../shared/hook/useTablerRevisionSolicitudPedido";
import SampleTablePagination from "@/shared/components/ui/SampleTablePagination";
import CustomTabPanel from "@/shared/components/ui/CustomTabPanel";
import { PaginationState } from "@tanstack/react-table";

const PAGE_SIZE = 10;

function useFilteredPagination(filtered: MaterialTransferido[]) {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: PAGE_SIZE,
    });

    useEffect(() => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, [filtered.length]);

    const paginatedData = useMemo(
        () =>
            filtered.slice(
                pagination.pageIndex * pagination.pageSize,
                (pagination.pageIndex + 1) * pagination.pageSize,
            ),
        [filtered, pagination],
    );

    return { paginatedData, pagination, setPagination, rowCount: filtered.length };
}

interface DetallesProductosProps {
    tabValue?: number;
    onTabChange?: (value: number) => void;
    onProductos?: (productos: MaterialTransferido[]) => void
}

export default function DetallesProductosEstados({ tabValue, onTabChange, onProductos }: DetallesProductosProps) {
    const productos = useCargarSeriesProductos((s) => s.productos);

    const { columns, /* columnsCompra, */ columnsEnviadosAcomprar, columnsPedidosCompletados } = useTableRevisionSolicitudPedido()

    const [internalValue, setInternalValue] = useState(0);
    const isControlled = tabValue !== undefined;
    const value = isControlled ? tabValue : internalValue;

    const productosListos = useMemo(
        () =>
            productos.reduce<MaterialTransferido[]>((acc, p) => {
                const stock = Number(p.producto_data?.stock_actual ?? 0);
                const pendiente = Number(p.cantidad_pendiente ?? 0)
                const aprobada = Number(p.cantidad_aprobada ?? 0);
                const transferido = Number(p.cantidad_transferido ?? 0);
                const cantidadDisponible = transferido > 0 ? pendiente : aprobada;
                // if (stock <= 0 || pendiente <= 0 || aprobada <= 0) return acc
                if (stock <= 0 || pendiente <= 0) return acc;
                acc.push({
                    ...p,
                    cantidad: Math.min(stock, cantidadDisponible)
                    //cantidad:Math.min(stock, pendiente)

                });
                return acc;
            }, []),
        [productos]
    )
    /* const productosPendientes = useMemo(
        () =>
            productos.reduce<MaterialTransferido[]>((acc, p) => {
                const stock = Number(p.producto_data?.stock_actual ?? 0);
                const cantidad = Number(p.cantidad_aprobada ?? 0);
                const pendiente = Number(p.cantidad_pendiente ?? (cantidad - stock))
                if (pendiente > 0 && stock < cantidad) {
                    acc.push({
                        ...p,
                        cantidad: p.cantidad_pendiente ?? (cantidad - stock),
                    });
                }
                return acc;
            }, []),
        [productos]
    ) */
    const productosPendientes = useMemo(
        () =>
            productos.reduce<MaterialTransferido[]>((acc, p) => {
                const stock = Number(p.producto_data?.stock_actual ?? 0);
                const cantidad = Number(p.cantidad_aprobada ?? 0);
                const transferido = Number(p.cantidad_transferido ?? 0);
                const pendienteReal = Math.max(cantidad - transferido - stock, 0);
                if (pendienteReal > 0) {
                    acc.push({
                        ...p,
                        cantidad: pendienteReal,
                    });
                }
                return acc;
            }, []),
        [productos]
    )
    const productosTransferidos = useMemo(
        () => productos.filter(p => {
            const pendiente = Number(p.cantidad_pendiente ?? 0);
            const cantidadTrasferido = Number(p.cantidad_transferido ?? 0)
            return (cantidadTrasferido > 0 || pendiente === 0);
        }),
        [productos]
    )

    const listosPagination = useFilteredPagination(productosListos);
    const pendientesPagination = useFilteredPagination(productosPendientes);
    const trasferidosPagination = useFilteredPagination(productosTransferidos);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        if (!isControlled) setInternalValue(newValue);
        onTabChange?.(newValue);
        if (newValue === 0) onProductos?.(productosListos);
        if (newValue === 1) onProductos?.(productosPendientes);
        if (newValue === 2) onProductos?.(productosTransferidos);
    };

    useEffect(() => {
        if (value === 0) onProductos?.(productosListos)
    }, [productosListos, value])

    return (
        <Grid container spacing={2}>
            <Grid item {...gridSizeMdLg12}>
                <Box sx={{ borderBottom: 2, borderColor: "divider" }}>
                    <Tabs value={value} onChange={handleChange}>
                        <Tab label={`📦 Por Transferir (${productosListos.length})`} {...a11yProps(0)} />
                        <Tab label={`🛒 Enviados a comprar (${productosPendientes.length})`} {...a11yProps(1)} />
                        <Tab label={`🚚 Transferidos(${productosTransferidos.length})`}{...a11yProps(2)} />
                    </Tabs>
                </Box>
            </Grid>
            <Grid item xs={12} mt={2}>
                <CustomTabPanel value={value} index={0}>
                    {listosPagination.paginatedData.length > 0 ? (
                        <SampleTablePagination
                            columns={columns}
                            data={listosPagination.paginatedData}
                            rowCount={listosPagination.rowCount}
                            pagination={listosPagination.pagination}
                            setPagination={listosPagination.setPagination}
                            rowNumber
                        />

                    ) :
                        <Alert variant='outlined' severity="info">
                            <AlertTitle>No hay productos por transferir</AlertTitle>
                            Aquí aparecerán los productos enviados a comprar una vez que cuenten con stock disponible en el sistema.
                            <br /><br />
                            <strong>Nota:</strong> Si no ve registros, es posible que las órdenes de compra aún estén en proceso o que todos los productos ya hayan sido transferidos.
                        </Alert>
                    }

                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                    {pendientesPagination.paginatedData.length > 0 ? (
                        <SampleTablePagination
                            columns={columnsEnviadosAcomprar}
                            data={pendientesPagination.paginatedData}
                            rowCount={pendientesPagination.rowCount}
                            pagination={pendientesPagination.pagination}
                            setPagination={pendientesPagination.setPagination}
                            rowNumber
                        />

                    ) :
                        <Alert severity="info">
                            <AlertTitle>Sin órdenes de compra</AlertTitle>
                            No se registran productos enviados a comprar actualmente.
                        </Alert>
                    }

                </CustomTabPanel>
                <CustomTabPanel value={value} index={2}>
                    {trasferidosPagination.paginatedData.length > 0 ? (
                        <SampleTablePagination
                            columns={columnsPedidosCompletados}
                            data={trasferidosPagination.paginatedData}
                            rowCount={trasferidosPagination.rowCount}
                            pagination={trasferidosPagination.pagination}
                            setPagination={trasferidosPagination.setPagination}
                            rowNumber
                        />

                    ) :
                        <Alert severity="info">
                            <AlertTitle>Sin transferencias registradas</AlertTitle>
                            Aún no se han realizado transferencias de productos en este solicitud.
                        </Alert>
                    }

                </CustomTabPanel>
            </Grid>
        </Grid>
    )
}